/**
 * Optional PostHog facade for agent lifecycle telemetry.
 *
 * Keep this module independent of the PostHog SDK because the web app loads it
 * from the inline browser snippet. Events must describe lifecycle state only: never
 * send prompt/response text, terminal output, attachment names, tokens, or
 * full error messages.
 */

import {
  getPostHogErrorContext,
  sanitizeDiagnostic,
  type PostHogErrorProperty,
} from "../../../src/lib/posthog-error-context";

type TelemetryValue = string | number | boolean | null | undefined;

type PostHogCapture = {
  capture?: (eventName: string, properties?: Record<string, TelemetryValue>) => unknown;
  captureException?: (error: unknown, properties?: Record<string, PostHogErrorProperty>) => unknown;
};

function getPostHog(): PostHogCapture | null {
  const candidate = (globalThis as { posthog?: unknown }).posthog;
  if (!candidate || typeof candidate !== "object") return null;
  const posthog = candidate as PostHogCapture;
  return typeof posthog.capture === "function" || typeof posthog.captureException === "function"
    ? posthog
    : null;
}
function sanitizeValue(value: TelemetryValue): TelemetryValue {
  if (typeof value !== "string") return value;
  return value.slice(0, 120);
}

export function captureAgentTelemetry(
  eventName: string,
  properties: Record<string, TelemetryValue> = {},
): void {
  const posthog = getPostHog();
  if (!posthog?.capture) return;

  try {
    const safeProperties = Object.fromEntries(
      Object.entries(properties)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, sanitizeValue(value)]),
    );
    posthog.capture(eventName, safeProperties);
  } catch {
    // Analytics must never change whether an agent message can be sent.
  }
}

function diagnosticError(error: unknown, fallback: string): Error {
  if (!(error instanceof Error)) return new Error(fallback);

  const safeMessage = sanitizeDiagnostic(error.message, 240) || fallback;
  const safeName = sanitizeDiagnostic(error.name, 80) || "Error";
  const safeError = new Error(safeMessage);
  safeError.name = safeName;

  // Preserve useful file/line information while replacing the original first
  // stack line, which can repeat an unbounded or user-controlled message.
  if (typeof error.stack === "string") {
    const stackLines = error.stack.split("\n").slice(1, 16)
      .map((line) => sanitizeDiagnostic(line, 300))
      .filter((line): line is string => Boolean(line));
    safeError.stack = [safeName + ": " + safeMessage, ...stackLines].join("\n");
  }
  return safeError;
}

/** Capture a handled agent failure with an actual bounded stack trace. */
export function captureAgentException(
  error: unknown,
  properties: Record<string, TelemetryValue> = {},
): void {
  const posthog = getPostHog();
  if (!posthog?.captureException) return;

  try {
    const safeProperties = Object.fromEntries(
      Object.entries({
        ...getPostHogErrorContext("web"),
        exception_capture: "agent_transport",
        ...properties,
      })
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, sanitizeValue(value as TelemetryValue)]),
    ) as Record<string, PostHogErrorProperty>;
    posthog.captureException(diagnosticError(error, "Agent operation failed"), safeProperties);
  } catch {
    // Analytics must never change whether an agent message can be sent.
  }
}

export function classifyAgentError(error: unknown): string {
  if (error && typeof error === "object") {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string" && code.trim()) {
      return code.trim().slice(0, 80);
    }
  }

  const message = error instanceof Error ? error.message.toLowerCase() : String(error || "").toLowerCase();
  if (message.includes("timed out") || message.includes("timeout")) return "timeout";
  if (message.includes("not connected") || message.includes("websocket")) return "transport";
  if (message.includes("auth")) return "authentication";
  if (message.includes("permission")) return "permission";
  if (message.includes("closed")) return "closed";
  return "unknown";
}
